import {
    AGENDA_CONTEXT_MAX_LENGTH,
    AGENDA_DEFAULT_TOPIC_DURATION_MINUTES,
    AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH,
    AGENDA_ITEM_MAX_DURATION_MINUTES,
    AGENDA_ITEM_TITLE_MAX_LENGTH,
    AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM,
    AGENDA_MAX_ITEMS,
    AGENDA_OPENING_STATEMENT_MAX_LENGTH,
} from './agendaLimits';

import type {
    MeetingAgenda,
    MeetingAgendaItem,
    MeetingAgendaUpdatePayload,
    MeetingDiscussionPoint,
} from '@/types/review/PostReviewMeeting';

import { toContentLanguage, type ContentLanguage } from '@/utils/contentLanguage';

/**
 * An agenda is written in one language, its own: the builder and its child components edit its plain
 * strings as they are, whatever the reader's language, and save them back with that language.
 */
export type AgendaPointView = MeetingDiscussionPoint;
export type AgendaItemView = MeetingAgendaItem;

/** What an agenda editor works on: the cycle review's agenda and a thematic meeting's agenda both fit. */
export type AgendaContent = Pick<MeetingAgenda, 'language' | 'openingStatement' | 'items'>;

/** The edited working copy as an update payload, in the agenda's own language, items in their shown order. */
export const toAgendaPayload = (
    items: AgendaItemView[],
    openingStatement: string,
    language: ContentLanguage,
): MeetingAgendaUpdatePayload => ({
    language,
    openingStatement,
    items: items.map((item, index) => ({
        id: item.id,
        title: item.title,
        durationMinutes: item.durationMinutes,
        order: index,
        isAiGenerated: item.isAiGenerated,
        discussionPoints: item.discussionPoints.map((point, pointIndex) => ({
            id: point.id,
            title: point.title,
            order: pointIndex,
            context: point.context,
            response: point.response,
            isAiGenerated: point.isAiGenerated,
        })),
    })),
});

/** What an alert of a generated plan warns the manager about. */
export type GeneratedAgendaAlertType = 'meetingFit' | 'tension' | 'boundary';

/**
 * A warning the AI addressed to the manager with a generated plan: a subject that belongs to another meeting or
 * process (meetingFit), a kept question that works against what the manager wants (tension), or a request the plan
 * does not carry out (boundary).
 */
export interface GeneratedAgendaAlert {
    type: GeneratedAgendaAlertType;
    text: string;
}

/** The AI-generated agenda, in the one language it was written in, parsed from the SignalR result JSON. */
export interface GeneratedAgenda {
    language: ContentLanguage;
    openingStatement: string;
    items: {
        title: string;
        durationMinutes: number;
        discussionPoints: { title: string; context: string | null }[];
    }[];
    /** What the AI changed and why, for the manager; empty when the plan comes without one. */
    summary: string;
    /** The AI's warnings to the manager; empty when there is none. */
    alerts: GeneratedAgendaAlert[];
}

const toText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const ALERT_TYPES: readonly GeneratedAgendaAlertType[] = ['meetingFit', 'tension', 'boundary'];

const isAlertType = (value: unknown): value is GeneratedAgendaAlertType =>
    ALERT_TYPES.includes(value as GeneratedAgendaAlertType);

/** The alerts of a generated plan: those of a known type with a text, the others left out. */
const toAlerts = (value: unknown): GeneratedAgendaAlert[] =>
    (Array.isArray(value) ? value : []).flatMap((rawAlert) => {
        const alertRecord = (rawAlert ?? {}) as Record<string, unknown>;
        const text = toText(alertRecord.text);
        return isAlertType(alertRecord.type) && text ? [{ type: alertRecord.type, text }] : [];
    });

/**
 * Parses the AI agenda JSON (plain strings, with the `language` they are written in) delivered over SignalR
 * into a GeneratedAgenda, with the summary and the alerts a thematic meeting's plan comes with. Returns null when
 * the payload can't be parsed or has no usable items.
 */
export const parseGeneratedAgenda = (
    content: string | undefined | null,
): GeneratedAgenda | null => {
    if (!content) return null;
    let model: unknown;
    try {
        model = JSON.parse(content);
    } catch {
        return null;
    }
    if (!model || typeof model !== 'object') return null;

    const root = model as Record<string, unknown>;
    const rawItems = Array.isArray(root.items) ? root.items : [];

    const items = rawItems
        .map((rawItem) => {
            const itemRecord = (rawItem ?? {}) as Record<string, unknown>;
            const duration =
                typeof itemRecord.durationMinutes === 'number' && itemRecord.durationMinutes > 0
                    ? Math.min(itemRecord.durationMinutes, AGENDA_ITEM_MAX_DURATION_MINUTES)
                    : AGENDA_DEFAULT_TOPIC_DURATION_MINUTES;
            const rawPoints = Array.isArray(itemRecord.discussionPoints)
                ? itemRecord.discussionPoints
                : [];
            const discussionPoints = rawPoints
                .map((rawPoint) => {
                    const pointRecord = (rawPoint ?? {}) as Record<string, unknown>;
                    const context = toText(pointRecord.context);
                    return { title: toText(pointRecord.title), context: context || null };
                })
                .filter((point) => point.title);
            return { title: toText(itemRecord.title), durationMinutes: duration, discussionPoints };
        })
        .filter((item) => item.title);

    if (items.length === 0) return null;

    return {
        language: toContentLanguage(typeof root.language === 'string' ? root.language : undefined),
        openingStatement: toText(root.openingStatement),
        items,
        summary: toText(root.summary),
        alerts: toAlerts(root.alerts),
    };
};

/**
 * Whether the generated agenda can be added to `current`: only into an agenda written in its own
 * language, so the agenda never mixes two. An empty agenda takes any; across languages, only Replace.
 */
export const canAddGeneratedAgenda = (
    current: Pick<AgendaContent, 'language'> | null | undefined,
    generated: Pick<GeneratedAgenda, 'language'>,
): boolean => !current || current.language === generated.language;

/**
 * The generated agenda's items as update-payload items (fresh client ids, AI-flagged), following
 * `startOrder` existing items and brought within the agenda limits: the model writes freely, and a payload
 * over any of them would be refused whole. The ids are always new, which is also what lets a Replace change
 * the agenda's language.
 */
const generatedItemsToPayload = (
    generated: GeneratedAgenda,
    startOrder: number,
): MeetingAgendaUpdatePayload['items'] =>
    generated.items.slice(0, Math.max(AGENDA_MAX_ITEMS - startOrder, 0)).map((item, index) => ({
        id: crypto.randomUUID(),
        title: item.title.slice(0, AGENDA_ITEM_TITLE_MAX_LENGTH),
        durationMinutes: item.durationMinutes,
        order: startOrder + index,
        isAiGenerated: true,
        discussionPoints: item.discussionPoints
            .slice(0, AGENDA_MAX_DISCUSSION_POINTS_PER_ITEM)
            .map((point, pointIndex) => ({
                id: crypto.randomUUID(),
                title: point.title.slice(0, AGENDA_DISCUSSION_POINT_TITLE_MAX_LENGTH),
                order: pointIndex,
                context: point.context && point.context.slice(0, AGENDA_CONTEXT_MAX_LENGTH),
                response: null,
                isAiGenerated: true,
            })),
    }));

/**
 * Builds the update payload from a generated agenda and the manager's integration choice:
 * - 'replace' → the generated opening statement + items only, in the generated agenda's language (all ids
 *   new, so the server accepts the language change).
 * - 'add' → the existing items followed by the generated items, as many as the agenda still has room for;
 *   the existing opening statement and language are kept. Only into an agenda of the generated language
 *   ({@link canAddGeneratedAgenda}): the integration step offers nothing else, and this refuses it too.
 */
export const mergeGeneratedAgenda = (
    mode: 'add' | 'replace',
    generated: GeneratedAgenda,
    current: AgendaContent | null | undefined,
): MeetingAgendaUpdatePayload => {
    if (mode === 'replace' || !current) {
        return {
            language: generated.language,
            openingStatement: generated.openingStatement.slice(
                0,
                AGENDA_OPENING_STATEMENT_MAX_LENGTH,
            ),
            items: generatedItemsToPayload(generated, 0),
        };
    }

    if (!canAddGeneratedAgenda(current, generated)) {
        throw new Error('A generated agenda can only be added to an agenda of its own language.');
    }

    const existing = toAgendaPayload(current.items, current.openingStatement, current.language);
    return {
        ...existing,
        items: [...existing.items, ...generatedItemsToPayload(generated, existing.items.length)],
    };
};
